import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import billingService from '../services/billingService';
import CurrencySelect from './CurrencySelect';
import { apiErrorMessage } from '../utils/apiError';
import { today } from './BillingSchedule';
import { SaveCancel } from './BillingTab';
import './AddCorporateProfilePage.css';
import './PaymentPorterPage.css';
import './BillingSchedule.css';
import './BillingTab.css';

// A HOTEL'S PRICES (V663) — Hotel > Basic > Prices. The named prices its billing documents use: a service, its charge,
// its unit price and currency, and the key a template reads it by ({{price_<key>}}).
//
// MONTHLY OR ONE-OFF (V676). A monthly line is a price per month, on the services table once per year of a billing
// period. A one-off line is charged once, for its unit price, on the day it names — and is on the document of the billing
// period covering that day only, so it is invoiced once.
//
// PARTNER-ONLY (teal marker, STYLE.md): the partner's deal with the hotel. The lambda returns the lines only to the
// hotel's partner's people and superadmins, and lets only its admins (and superadmins) change them (canEditPrices).
//
// EDITABLE WHEREVER IT IS SHOWN, for those who may change prices (canEditPrices). On the hotel's edit page it is saved by
// Update Hotel through the ref — { pending() → null | { problem }, save() }; on the view page it has its own Add, Save
// and Cancel (it was read-only there, so "Add price" could not be found without first pressing Edit Hotel). Saved
// through PUT /billing/hotel/{id} with { priceLines } alone, which touches nothing else about the hotel's billing.

const DEFAULT_CURRENCY = 'EUR';
const keyOf = (text) => String(text || '').toLowerCase().replace(/[^a-z0-9]+/g, '_').replace(/^_+|_+$/g, '').slice(0, 60);
const toRows = (lines) => (lines || []).map((l, i) => ({
  _k: `l${i}-${l.id || ''}`, service: l.service || '', priceKey: l.priceKey || '',
  unitPrice: l.unitPrice == null ? '' : String(l.unitPrice), currency: l.currency || DEFAULT_CURRENCY,
  chargeType: l.chargeType || 'MONTHLY', chargeOn: l.chargeOn || '',
}));
const keyOfRows = (rows) => JSON.stringify(rows.map(r => [r.service.trim(), (r.priceKey || keyOf(r.service)).trim(),
  String(r.unitPrice).trim(), r.currency.trim().toUpperCase(), r.chargeType, r.chargeType === 'ONE_OFF' ? r.chargeOn : null]));

// Why the lines cannot be saved, or null — the lambda checks the same.
const problemOf = (rows) => {
  const keys = new Set();
  for (const [i, r] of rows.entries()) {
    if (!r.service.trim()) return `Prices: line ${i + 1} needs a service name.`;
    const key = keyOf(r.priceKey || r.service);
    if (!key) return `Prices: line ${i + 1} needs a key made of letters or digits.`;
    if (keys.has(key)) return `Prices: two lines have the key "${key}" — each needs its own.`;
    keys.add(key);
    const n = Number(r.unitPrice);
    if (r.unitPrice === '' || !Number.isFinite(n) || n < 0) return `Prices: line ${i + 1} needs a unit price of 0 or more.`;
    if (!/^[A-Za-z]{3}$/.test(r.currency.trim())) return `Prices: line ${i + 1} needs a currency.`;
    if (r.chargeType === 'ONE_OFF' && !/^\d{4}-\d{2}-\d{2}$/.test(r.chargeOn || '')) return `Prices: line ${i + 1} is one-off — give the day it is charged.`;
  }
  return null;
};

// WHERE A ONE-OFF CHARGE LANDS, said under its date — the same rule as /opt/utils/billingdocs servicesFor: the billing
// period covering the day; a day before the first period goes on the first period's document; a day after the last
// period ends, or a hotel with no periods, is on no document at all — which used to be silent.
const oneOffNote = (day, periods) => {
  if (!day) return null;
  const list = (periods || []).filter(p => p && p.from).slice().sort((a, b) => (a.from < b.from ? -1 : 1));
  if (!list.length) return { warn: true, text: 'This hotel has no billing period yet, so this charge is on no order form. Add one on the Billing tab.' };
  const first = list[0];
  if (day < first.from) return { warn: false, text: `On the first billing period's order form (from ${first.from}).` };
  const hit = list.find(p => day >= p.from && (!p.to || day <= p.to));
  if (hit) return { warn: false, text: `On the order form of the billing period from ${hit.from}.` };
  return { warn: true, text: `No billing period covers ${day} (the last ends ${list[list.length - 1].to}), so this charge is on no order form.` };
};

const HotelPrices = forwardRef(({ hotelId, mode }, ref) => {
  const [data, setData] = useState(null);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!hotelId) return;
    let live = true;
    billingService.get('hotel', hotelId)
      .then(res => { if (live) { setData(res); setRows(toRows(res.priceLines)); } })
      .catch(err => { if (live) setError(apiErrorMessage(err, 'Failed to load the prices')); });
    return () => { live = false; };
  }, [hotelId]);

  const editable = !!(data && data.canEditPrices);
  // On the edit page the page's Update button saves it; anywhere else it saves itself.
  const pageSaves = mode === 'edit';
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const changed = !!data && keyOfRows(rows) !== keyOfRows(toRows(data.priceLines));

  useImperativeHandle(ref, () => ({
    pending: () => (pageSaves && editable && changed ? { problem: problemOf(rows) } : null),
    save: async () => {
      if (!pageSaves || !editable || !changed) return;
      try {
        await persist();
      } catch (err) {
        throw new Error(apiErrorMessage(err, 'Failed to save the prices'));
      }
    },
  }));

  async function persist() {
    const res = await billingService.save('hotel', hotelId, {
      priceLines: rows.map(r => ({ service: r.service.trim(), priceKey: keyOf(r.priceKey || r.service),
        unitPrice: Number(r.unitPrice), currency: r.currency.trim().toUpperCase(),
        chargeType: r.chargeType, chargeOn: r.chargeType === 'ONE_OFF' ? r.chargeOn : null })),
    });
    setData(res);
    setRows(toRows(res.priceLines));
  }
  const saveHere = async () => {
    setSaving(true); setSaveError(null);
    try { await persist(); } catch (err) { setSaveError(apiErrorMessage(err, 'Failed to save the prices')); } finally { setSaving(false); }
  };

  if (error) return <div className="acp-error-banner">{error}</div>;
  if (!data) return <p className="billing-muted">Loading prices…</p>;
  // Not one of the hotel's partner's people: the lambda sent no prices, and there is nothing to show.
  if (data.priceLines === undefined) return <p className="billing-muted">Billing prices are visible to the hotel’s partner only.</p>;

  const set = (k, patch) => setRows(list => list.map(r => (r._k === k ? { ...r, ...patch } : r)));
  const add = () => setRows(list => [...list, {
    _k: `new-${Date.now()}-${list.length}`, service: '', priceKey: '', unitPrice: '', currency: (list[0] && list[0].currency) || DEFAULT_CURRENCY,
    chargeType: 'MONTHLY', chargeOn: '',
  }]);
  const remove = (k) => setRows(list => list.filter(r => r._k !== k));

  return (
    <div className="billing-tab partner-only-panel">
      <div className="billing-section">
        <div className="billing-payer-header billing-list-header">
          <div>
            <h3 className="billing-payer-title">Prices</h3>
            <p className="billing-muted">Used by billing documents: a template shows them in its services table, or one by
              one as {'{{price_<key>}}'}. A monthly price is per month; a one-off charge is billed once, on the
              document of the billing period covering the day it is charged.</p>
          </div>
          {editable && (
            <button type="button" className="pp-rules-add-btn" onClick={add}>
              <svg width="13" height="13" viewBox="0 0 16 16" fill="none" style={{ flexShrink: 0 }}>
                <path d="M8 3v10M3 8h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
              Add price
            </button>
          )}
        </div>
        {rows.length === 0 ? (
          <p className="billing-muted">No prices yet.</p>
        ) : (
          <table className="bsch-table hotel-prices">
            <thead>
              <tr><th>Service</th><th>Key</th><th>Charge</th><th>Unit price</th><th>Currency</th>{editable && <th aria-label="Actions" />}</tr>
            </thead>
            <tbody>
              {rows.map(r => (
                <tr key={r._k}>
                  <td>
                    <input className="acp-input" value={r.service} disabled={!editable} aria-label="Service"
                      placeholder="e.g. Pegasus / Thynk 2-way Integration" onChange={e => set(r._k, { service: e.target.value })} />
                  </td>
                  <td>
                    <input className="acp-input" value={r.priceKey} disabled={!editable} aria-label="Key"
                      placeholder={keyOf(r.service) || 'from the service'} onChange={e => set(r._k, { priceKey: keyOf(e.target.value) })} />
                  </td>
                  <td>
                    <select className="acp-select" value={r.chargeType} disabled={!editable} aria-label="Charge"
                      onChange={e => set(r._k, { chargeType: e.target.value, chargeOn: e.target.value === 'ONE_OFF' ? (r.chargeOn || today()) : '' })}>
                      <option value="MONTHLY">Monthly</option>
                      <option value="ONE_OFF">One-off</option>
                    </select>
                    {r.chargeType === 'ONE_OFF' && (
                      <input className="acp-input" type="date" value={r.chargeOn} disabled={!editable} aria-label="Charged on"
                        title="The day it is charged — it is on the document of the billing period covering this day"
                        style={{ marginTop: '0.35rem' }} onChange={e => set(r._k, { chargeOn: e.target.value })} />
                    )}
                    {r.chargeType === 'ONE_OFF' && (() => {
                      const note = oneOffNote(r.chargeOn, data.periods);
                      return note && (
                        <p className="billing-muted" style={{ margin: '0.3rem 0 0', ...(note.warn ? { color: '#dc2626' } : {}) }}>{note.text}</p>
                      );
                    })()}
                  </td>
                  <td>
                    <input className="acp-input" type="number" min="0" step="0.01" value={r.unitPrice} disabled={!editable}
                      aria-label={r.chargeType === 'ONE_OFF' ? 'One-off amount' : 'Unit price per month'}
                      placeholder={r.chargeType === 'ONE_OFF' ? 'amount' : 'per month'}
                      onChange={e => set(r._k, { unitPrice: e.target.value })} />
                  </td>
                  <td>
                    <CurrencySelect className="acp-select hotel-prices-currency" value={r.currency} disabled={!editable}
                      onChange={e => set(r._k, { currency: e.target.value })} />
                  </td>
                  {editable && (
                    <td>
                      <button type="button" className="bsch-icon-btn" title="Remove this price" aria-label="Remove this price"
                        onClick={() => remove(r._k)}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="3 6 5 6 21 6"/>
                          <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                        </svg>
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {editable && !pageSaves && changed && (
          <>
            {problemOf(rows) && <div className="acp-error-banner">{problemOf(rows)}</div>}
            {saveError && <div className="acp-error-banner">{saveError}</div>}
            <SaveCancel saving={saving} blocked={!!problemOf(rows)}
              onCancel={() => { setRows(toRows(data.priceLines)); setSaveError(null); }} onSave={saveHere} />
          </>
        )}
      </div>
    </div>
  );
});
HotelPrices.displayName = 'HotelPrices';

export default HotelPrices;
